/**
 * Translate the voucher from the voucher API (state.voucher.voucher) into the
 * holocron order-calc voucher shape (spec §4).
 *
 * This is a port of amen app/services/order-calc/to-module-voucher.js. Keep
 * the two the same. A later task can move one copy into holocron, so that
 * amen and the frontend share it.
 *
 * The voucher API returns the rule fields already merged with the global
 * exclusion lists (D5), so this function does not merge them again.
 *
 * Units (D18):
 *   - percent is 0–100 on the voucher; the module wants 0–1.
 *   - amount[ccy] is major units; the module wants minor units.
 *   - min_spend[ccy] and max_spend[ccy] are already minor units. The value 0
 *     means "no threshold" (D22), so it becomes null.
 */

/** Discount document gate key → module types key. */
export const TYPE_GATE_KEYS = {
  game: 'game',
  dlc: 'dlc',
  bundle: 'bundle',
  book: 'book',
  software: 'software',
  comic: 'comic',
  audio: 'audio',
  video: 'video',
  star_deal: 'star_deal',
  pick_and_mix: 'pick_and_mix',
  gift_cards: 'gift-card', // D18 alias: legacy discount key → module enum key
};

// The API can return populated product objects or plain ids (cartcalc.js:133)
const toIds = list => (list ?? []).map(entry => String(entry?._id ?? entry));

/**
 * @param   {Object} discount     The voucher from the voucher API.
 * @param   {string} currencyCode ISO 4217 code of the cart.
 * @returns {Object}              A module-shape voucher.
 * @throws  {Error}               On a missing discount or currencyCode, or an
 *                                unrecognised type_of_discount value.
 */
export default function toModuleVoucher(discount, currencyCode) {
  if (!discount) {
    throw new Error('Missing discount');
  }

  if (!currencyCode) {
    throw new Error('Missing currencyCode');
  }

  const typeOfDiscount = discount.type_of_discount ?? 'percent';

  if (!['money', 'percent'].includes(typeOfDiscount)) {
    throw new Error('Invalid value for discount.type_of_discount');
  }

  const currencyFormat = (obj) => {
    if (!obj) return null;

    const major = obj[currencyCode];

    if (!major && major !== 0) return null;

    return Math.round(major * 100);
  };

  const currencySelect = (obj) => {
    if (!obj) return null;

    const value = obj[currencyCode];

    if (value == null || value === 0) return null;

    return Math.round(value);
  };

  const voucher = {
    typeOfDiscount,
    excludedProducts: toIds(discount.excluded_products),
    requiredProducts: toIds(discount.required_products),
    excludedSuppliers: toIds(discount.excluded_suppliers),
    requiredSuppliers: toIds(discount.required_suppliers),
    excludedFacets: (discount.excluded_facets ?? []).map(String),
    requiredFacets: (discount.required_facets ?? []).map(String),
    fullPriceOnly: discount.full_price_only ?? false,
    code: discount.code,
    preorder: discount.preorder ?? false,
    amount: null,
    percent: null,
    minSpend: currencySelect(discount.min_spend),
    maxSpend: currencySelect(discount.max_spend),
    types: {},
  };

  if (voucher.typeOfDiscount === 'money') {
    voucher.amount = currencyFormat(discount.amount);
  }

  if (voucher.typeOfDiscount === 'percent') {
    voucher.percent = discount.percent / 100;
  }

  Object.keys(TYPE_GATE_KEYS).forEach((oldKey) => {
    if (discount[oldKey]) {
      voucher.types[TYPE_GATE_KEYS[oldKey]] = true;
    }
  });

  return voucher;
}
